import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  Lightbulb,
  TrendingUp,
  AlertTriangle,
  Clock,
  HelpCircle,
  Calendar,
  Shield,
  BarChart3,
  Brain,
  Gauge,
  Focus,
  Users,
} from "lucide-react";
import { TradeEntry, BehaviorEntry } from "../../types/tradingJournal";

interface TradeInsightsProps {
  entries: TradeEntry[];
  behaviorEntries?: BehaviorEntry[];
}

const TradeInsights = ({
  entries,
  behaviorEntries = [],
}: TradeInsightsProps) => {
  const insights = useMemo(() => {
    if (entries.length < 5 && behaviorEntries.length < 3) {
      return [
        {
          type: "info",
          message:
            "Add more trades to see AI-powered insights about your trading patterns.",
        },
      ];
    }

    const insights = [];

    // Find best performing setup
    const setupMap = new Map<
      string,
      { count: number; wins: number; totalPnl: number; totalRoi: number }
    >();
    entries.forEach((entry) => {
      if (entry.setup) {
        const existing = setupMap.get(entry.setup) || {
          count: 0,
          wins: 0,
          totalPnl: 0,
          totalRoi: 0,
        };
        setupMap.set(entry.setup, {
          count: existing.count + 1,
          wins: existing.wins + (entry.pnl > 0 ? 1 : 0),
          totalPnl: existing.totalPnl + entry.pnl,
          totalRoi: existing.totalRoi + entry.roi,
        });
      }
    });

    const setupStats = Array.from(setupMap.entries()).map(([setup, stats]) => ({
      setup,
      count: stats.count,
      winRate: (stats.wins / stats.count) * 100,
      avgRoi: stats.totalRoi / stats.count,
      totalPnl: stats.totalPnl,
    }));

    if (setupStats.length > 0) {
      const bestSetup = setupStats.reduce(
        (best, current) =>
          current.count >= 3 && current.winRate > best.winRate ? current : best,
        setupStats[0]
      );

      if (bestSetup.winRate > 60) {
        insights.push({
          type: "success",
          message: `Your highest win rate setup is "${
            bestSetup.setup
          }" with ${bestSetup.winRate.toFixed(
            1
          )}% wins and average ROI of ${bestSetup.avgRoi.toFixed(2)}%.`,
        });
      }
    }

    // Find most common mistake in losing trades
    const mistakeMap = new Map<string, number>();
    const losingTrades = entries.filter((entry) => entry.pnl < 0);

    losingTrades.forEach((entry) => {
      if (entry.mistakes && entry.mistakes.length > 0) {
        entry.mistakes.forEach((mistake) => {
          mistakeMap.set(mistake, (mistakeMap.get(mistake) || 0) + 1);
        });
      }
    });

    if (mistakeMap.size > 0) {
      const mistakes = Array.from(mistakeMap.entries()).sort(
        (a, b) => b[1] - a[1]
      );

      if (mistakes.length > 0) {
        insights.push({
          type: "warning",
          message: `Your most common mistake on losing trades is "${mistakes[0][0]}" occurring in ${mistakes[0][1]} trades.`,
        });
      }
    }

    // Find best trading hour
    const hourMap = new Map<number, { count: number; pnl: number }>();
    entries.forEach((entry) => {
      if (entry.entryTime) {
        const hour = parseInt(entry.entryTime.split(":")[0]);
        const existing = hourMap.get(hour) || { count: 0, pnl: 0 };
        hourMap.set(hour, {
          count: existing.count + 1,
          pnl: existing.pnl + entry.pnl,
        });
      }
    });

    if (hourMap.size > 0) {
      const hourStats = Array.from(hourMap.entries())
        .map(([hour, stats]) => ({
          hour,
          count: stats.count,
          avgPnl: stats.pnl / stats.count,
        }))
        .filter((stat) => stat.count >= 3)
        .sort((a, b) => b.avgPnl - a.avgPnl);

      if (hourStats.length > 0) {
        const bestHour = hourStats[0];
        insights.push({
          type: "time",
          message: `You perform best when trading at ${
            bestHour.hour
          }:00 with an average P&L of ₹${bestHour.avgPnl.toFixed(
            2
          )} per trade.`,
        });
      }
    }

    // Analyze behavior data if available
    if (behaviorEntries.length >= 5) {
      // Check for emotional pattern
      const calmEntries = behaviorEntries.filter(
        (e) => e.emotionallyCalm === "Yes"
      );
      const notCalmEntries = behaviorEntries.filter(
        (e) => e.emotionallyCalm === "No"
      );

      if (calmEntries.length >= 3 && notCalmEntries.length >= 2) {
        const calmAvgPnL =
          calmEntries.reduce((sum, e) => sum + e.dailyPnL, 0) /
          calmEntries.length;
        const notCalmAvgPnL =
          notCalmEntries.reduce((sum, e) => sum + e.dailyPnL, 0) /
          notCalmEntries.length;

        if (calmAvgPnL > 0 && calmAvgPnL > notCalmAvgPnL * 1.5) {
          insights.push({
            type: "behavior",
            message: `Trading while emotionally calm leads to ₹${calmAvgPnL.toFixed(
              0
            )} daily P&L vs ₹${notCalmAvgPnL.toFixed(0)} when not calm.`,
          });
        }
      }

      // Check for rushing trading pattern
      const rushingEntries = behaviorEntries.filter(
        (e) => e.wasRushing === "Yes"
      );
      const notRushingEntries = behaviorEntries.filter(
        (e) => e.wasRushing === "No"
      );

      if (rushingEntries.length >= 2 && notRushingEntries.length >= 2) {
        const rushingAvgPnL =
          rushingEntries.reduce((sum, e) => sum + e.dailyPnL, 0) /
          rushingEntries.length;
        const notRushingAvgPnL =
          notRushingEntries.reduce((sum, e) => sum + e.dailyPnL, 0) /
          notRushingEntries.length;

        if (notRushingAvgPnL > rushingAvgPnL) {
          insights.push({
            type: "focus",
            message: `Trading without rushing yields ₹${notRushingAvgPnL.toFixed(
              0
            )} vs ₹${rushingAvgPnL.toFixed(0)} when rushing into trades.`,
          });
        }
      }

      // Check for revenge trading pattern if data exists
      const revengeEntries = behaviorEntries.filter(
        (e) => e.revengeTrading === "Yes"
      );
      if (revengeEntries.length >= 2) {
        const revengeAvgPnL =
          revengeEntries.reduce((sum, e) => sum + e.dailyPnL, 0) /
          revengeEntries.length;

        if (revengeAvgPnL < 0) {
          insights.push({
            type: "caution",
            message: `Revenge trading has cost you an average of ₹${Math.abs(
              revengeAvgPnL
            ).toFixed(0)} per occurrence.`,
          });
        }
      }

      // Time of day pattern
      const afternoonEntries = behaviorEntries.filter((e) =>
        e.tradingTimes?.includes("After 2 PM")
      );
      const morningEntries = behaviorEntries.filter((e) =>
        e.tradingTimes?.includes("Morning")
      );

      if (afternoonEntries.length >= 3 && morningEntries.length >= 3) {
        const afternoonAvgPnL =
          afternoonEntries.reduce((sum, e) => sum + e.dailyPnL, 0) /
          afternoonEntries.length;
        const morningAvgPnL =
          morningEntries.reduce((sum, e) => sum + e.dailyPnL, 0) /
          morningEntries.length;

        if (morningAvgPnL > 0 && afternoonAvgPnL < 0) {
          insights.push({
            type: "insight",
            message: `Morning trading (₹${morningAvgPnL.toFixed(
              0
            )}) significantly outperforms afternoon trading (₹${afternoonAvgPnL.toFixed(
              0
            )}).`,
          });
        }
      }

      // Trade on setup vs. instinct
      const setupEntries = behaviorEntries.filter(
        (e) => e.tradedOnSetup === "Setup"
      );
      const instinctEntries = behaviorEntries.filter(
        (e) => e.tradedOnSetup === "Instinct"
      );

      if (setupEntries.length >= 3 && instinctEntries.length >= 3) {
        const setupAvgPnL =
          setupEntries.reduce((sum, e) => sum + e.dailyPnL, 0) /
          setupEntries.length;
        const instinctAvgPnL =
          instinctEntries.reduce((sum, e) => sum + e.dailyPnL, 0) /
          instinctEntries.length;

        const betterApproach =
          setupAvgPnL > instinctAvgPnL ? "setup-based" : "instinct-based";
        const difference = Math.abs(setupAvgPnL - instinctAvgPnL);

        if (difference > 2000) {
          insights.push({
            type: "strategy",
            message: `Your ${betterApproach} trading performs ₹${difference.toFixed(
              0
            )} better on average than the alternative approach.`,
          });
        }
      }
    }

    // Add insights about consistency or improvement
    const sortedEntries = [...entries].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    if (sortedEntries.length >= 10) {
      const firstHalf = sortedEntries.slice(
        0,
        Math.floor(sortedEntries.length / 2)
      );
      const secondHalf = sortedEntries.slice(
        Math.floor(sortedEntries.length / 2)
      );

      const firstHalfWinRate =
        firstHalf.filter((e) => e.pnl > 0).length / firstHalf.length;
      const secondHalfWinRate =
        secondHalf.filter((e) => e.pnl > 0).length / secondHalf.length;

      if (secondHalfWinRate > firstHalfWinRate * 1.2) {
        insights.push({
          type: "improvement",
          message: `Your win rate has improved from ${(
            firstHalfWinRate * 100
          ).toFixed(1)}% to ${(secondHalfWinRate * 100).toFixed(
            1
          )}% in your recent trades.`,
        });
      }
    }

    return insights;
  }, [entries, behaviorEntries]);

  const getIconForInsightType = (type: string) => {
    switch (type) {
      case "success":
        return <TrendingUp className="h-5 w-5 text-green-400" />;
      case "warning":
        return <AlertTriangle className="h-5 w-5 text-amber-400" />;
      case "time":
        return <Clock className="h-5 w-5 text-blue-400" />;
      case "improvement":
        return <TrendingUp className="h-5 w-5 text-purple-400" />;
      case "behavior":
        return <Shield className="h-5 w-5 text-indigo-400" />;
      case "caution":
        return <AlertTriangle className="h-5 w-5 text-red-400" />;
      case "insight":
        return <BarChart3 className="h-5 w-5 text-cyan-400" />;
      case "focus":
        return <Focus className="h-5 w-5 text-orange-400" />;
      case "strategy":
        return <Brain className="h-5 w-5 text-violet-400" />;
      case "social":
        return <Users className="h-5 w-5 text-pink-400" />;
      case "performance":
        return <Gauge className="h-5 w-5 text-yellow-400" />;
      default:
        return <Lightbulb className="h-5 w-5 text-white/70" />;
    }
  };

  const getBadgeForInsightType = (type: string) => {
    switch (type) {
      case "success":
        return (
          <Badge className="bg-green-900/30 text-green-400 hover:bg-green-900/50">
            Performance
          </Badge>
        );
      case "warning":
        return (
          <Badge className="bg-amber-900/30 text-amber-400 hover:bg-amber-900/50">
            Improvement Area
          </Badge>
        );
      case "time":
        return (
          <Badge className="bg-blue-900/30 text-blue-400 hover:bg-blue-900/50">
            Timing
          </Badge>
        );
      case "improvement":
        return (
          <Badge className="bg-purple-900/30 text-purple-400 hover:bg-purple-900/50">
            Progress
          </Badge>
        );
      case "behavior":
        return (
          <Badge className="bg-indigo-900/30 text-indigo-400 hover:bg-indigo-900/50">
            Psychology
          </Badge>
        );
      case "caution":
        return (
          <Badge className="bg-red-900/30 text-red-400 hover:bg-red-900/50">
            Risk Alert
          </Badge>
        );
      case "insight":
        return (
          <Badge className="bg-cyan-900/30 text-cyan-400 hover:bg-cyan-900/50">
            Pattern
          </Badge>
        );
      case "focus":
        return (
          <Badge className="bg-orange-900/30 text-orange-400 hover:bg-orange-900/50">
            Focus
          </Badge>
        );
      case "strategy":
        return (
          <Badge className="bg-violet-900/30 text-violet-400 hover:bg-violet-900/50">
            Strategy
          </Badge>
        );
      case "social":
        return (
          <Badge className="bg-pink-900/30 text-pink-400 hover:bg-pink-900/50">
            Social
          </Badge>
        );
      case "performance":
        return (
          <Badge className="bg-yellow-900/30 text-yellow-400 hover:bg-yellow-900/50">
            Analytics
          </Badge>
        );
      default:
        return <Badge className="bg-white/10 hover:bg-white/20">Insight</Badge>;
    }
  };

  return (
    <Card className="intellectia-card h-full flex flex-col">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Lightbulb className="h-5 w-5 mr-2 text-market-teal" />
          Smart Trade Insights
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1">
        <ul className="space-y-4">
          {insights.map((insight, idx) => (
            <li key={idx} className="flex space-x-3">
              <div className="mt-0.5">
                {getIconForInsightType(insight.type)}
              </div>
              <div>
                <p className="text-white/90">{insight.message}</p>
                <div className="mt-1">
                  {getBadgeForInsightType(insight.type)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

export default TradeInsights;
